import React from "react";
import { type NextPage } from "next";
import { sectionSpacingY } from "components/section";
import { Page } from "components/page";
import {
  DestinationMarquee,
  IndustryLeaderAwards,
  SecurityFeatures,
  TraditionalCdpVsHightouch,
} from "components/content";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { CustomerStatsHeroAnimation, GradientHalfHero } from "components/hero";
import { StackedLogos } from "components/logos";
import { AuraCardQuote, CenteredQuote } from "components/quotes";
import { Text } from "@chakra-ui/react";
import { MediaNetworkFeatures, RetailProductSlideshow, RetailTabs } from "components/solutions/retail";
import { SolidPlate } from "ui/plates";
import { BookDemoButton } from "components/event-tracking";
import heroBgPhoto from "../../../public/assets/solutions/retail/hero-bg-photo.png";

const Retail: NextPage<LandingPageProps> = ({ page, announcementBanner, destinations, customers }) => {
  const petsmart = getSafeCustomer(customers, "petsmart");
  const weightWatchers = getSafeCustomer(customers, "weight-watchers");
  const bol = getSafeCustomer(customers, "bol");

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.green.150"
    >
      <GradientHalfHero
        variant="green"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        media={
          <CustomerStatsHeroAnimation
            bgPhoto={{ src: heroBgPhoto, alt: "Two corgi dogs laying happily on their backs in the grass." }}
            primaryCaseStudyStat={{
              customer: petsmart,
              percentValue: 200,
              description: "Boost in engagement",
            }}
            secondaryCaseStudyStat={{
              customer: weightWatchers,
              percentValue: 52,
              description: "Increase in acquisition",
            }}
          />
        }
        mb={{ sm: "small", md: "big", lg: 0 }}
      />

      {page.customers && (
        <StackedLogos
          title="Trusted by leading retailers"
          customers={page.customers}
          mobileCustomers={page.customers}
          my="0"
        />
      )}

      <RetailTabs />

      <AuraCardQuote
        variant="blue"
        my="0"
        pb={sectionSpacingY}
        quote={{
          quoteText: (
            <>
              We’re activating{" "}
              <Text as="strong" fontWeight="bold">
                millions of records
              </Text>{" "}
              every day to personalize journeys for the 65+ million members in our loyalty program.
            </>
          ),
          author: { name: "Bradley Breuer", job: "VP Marketing" },
          company: {
            name: "Petsmart",
            logo: petsmart.logoUrl,
          },
          cta: { text: "Read the story", href: "/customers/petsmart" },
        }}
      />

      <SolidPlate theme="dark" bgColor="new.green.950">
        <RetailProductSlideshow />

        <MediaNetworkFeatures />

        <CenteredQuote
          theme="dark"
          quoteText="Hightouch has completely unlocked how we deliver relevancy to users across our website and our mobile app, but there’s also huge potential for us to monetize users outside of our domain."
          author={{
            name: "Kai Kickuth",
            job: "Sr. Product Manager",
          }}
          company={{
            name: "Bol.com",
            logo: bol.logoUrl,
          }}
          cta={{
            href: "/customers/bol",
            text: "Read the story",
          }}
          mt={{ base: "huge", md: "hugest" }}
          mb={{ base: "small", md: "big" }}
          maxW="830px"
        />
      </SolidPlate>

      <TraditionalCdpVsHightouch
        title="The only comprehensive Customer Data Platform for retail"
        description="Don’t put yourself through a long, painful implementation with a traditional CDP just to fail months later. Get started with your existing data today."
        titleMaxW="3xl"
        actions={<BookDemoButton text="Talk to an expert" />}
        my="0"
        pt={sectionSpacingY}
      />

      <SecurityFeatures
        title="Composable CDPs are more secure"
        description="Hightouch doesn’t store your data. Instead we simply read from your data warehouse where it stays safe and sound."
        textMaxW="530px"
      />

      <IndustryLeaderAwards />

      <DestinationMarquee
        title="Connect to any media platform"
        description="Send data to any platform you run ads in."
        ctaText="See all retail integrations"
        ctaSearchParams="?category=E-commerce"
        textMaxW="100%"
        destinations={destinations}
        my={0}
        py={sectionSpacingY}
      />
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/retail", {
  loadDestinations: "E-commerce",
  loadCustomers: ["petsmart", "weight-watchers", "bol"],
});

export default Retail;
